import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import './Register.css';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    security_pin: '',
    refer_code: ''
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.username || !formData.mobile || !formData.password || !formData.security_pin) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.security_pin.length !== 4) {
      toast.error('Security PIN must be 4 digits');
      return;
    }

    setLoading(true);
    const result = await register(formData);
    setLoading(false);

    if (result.success) {
      toast.success('Registration successful! Please login.');
      navigate('/login');
    } else {
      toast.error(result.error || 'Registration failed');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Input sanitization
    let sanitizedValue = value;
    
    if (name === 'mobile') {
      sanitizedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'security_pin') {
      sanitizedValue = value.replace(/\D/g, '').slice(0, 4);
    } else if (name === 'refer_code') {
      sanitizedValue = value.toUpperCase();
    }
    
    setFormData({ ...formData, [name]: sanitizedValue });
  };

  return (
    <div className="register-container">
      <div className="register-card">
        {/* LOGO SECTION - Same as login */}
        <div className="register-logo">
          <div className="logo-container">
            <img 
              src="/logo1.png"  // Path to your logo in public folder
              alt="BetPro Logo"
              className="logo-image"
              onError={(e) => {
                // Fallback if logo doesn't exist
                e.target.style.display = 'none';
                e.target.parentNode.innerHTML = `
                  <div class="logo-fallback">
                    <div style="
                      width: 80px;
                      height: 80px;
                      background: var(--gradient-primary);
                      border-radius: 12px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      font-size: 32px;
                      color: white;
                      font-weight: bold;
                      box-shadow: var(--shadow-soft);
                    ">
                      BP
                    </div>
                  </div>
                `;
              }}
            />
          </div>
         
        </div>

        <div className="register-header">
          <h2>Create Account</h2>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          {/* Single column layout instead of grid */}
          <div className="form-group">
            <label htmlFor="username">Username *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter username"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="mobile">Mobile Number *</label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              placeholder="Enter 10-digit mobile number"
              maxLength="10"
              pattern="[0-9]{10}"
              inputMode="numeric"
              required
              disabled={loading}
            />
            <div className="input-hint">10 digits only</div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter password (min. 6 characters)"
              minLength="6"
              required
              disabled={loading}
            />
            <div className="input-hint">Minimum 6 characters</div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter password"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="security_pin">Security PIN *</label>
            <input
              type="password"
              id="security_pin"
              name="security_pin"
              value={formData.security_pin}
              onChange={handleChange}
              placeholder="Enter 4-digit PIN"
              maxLength="4"
              pattern="[0-9]*"
              inputMode="numeric"
              required
              disabled={loading}
            />
            <div className="input-hint">4 digits only</div>
          </div>

          <div className="form-group">
            <label htmlFor="refer_code">Referral Code (Optional)</label>
            <input
              type="text"
              id="refer_code"
              name="refer_code"
              value={formData.refer_code}
              onChange={handleChange}
              placeholder="Enter referral code"
              disabled={loading}
            />
          </div>

          <button 
            type="submit" 
            className={`register-submit-btn ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner"></div>
                Registering...
              </>
            ) : (
              'CREATE ACCOUNT'
            )}
          </button>

          <div className="register-footer">
            <p>Already have an account? <Link to="/login">Login here</Link></p>
            <p className="terms-link">
              By registering, you agree to our{' '}
              <Link to="/">Terms of Service</Link> and{' '}
              <Link to="/">Privacy Policy</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;